import axios from 'axios';
import { setupAxiosInterceptor } from './auth/axiosConfig';
import config from './config';

const chatApi = axios.create({
  baseURL: config.API_BASE_URL + '/cellcog',
});

setupAxiosInterceptor(chatApi);

// Add this utility function to check for CellCog access errors
export const isCellCogAccessError = (error) => {
  return (
    error.response &&
    error.response.status === 403 &&
    error.response.data.detail === "CellCog access not enabled for this user"
  );
};

// Apply the wrapper to all chat API functions
export const getChats = (page = 1, pageSize = 20, projectId = null) => {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString()
  });

  // Only add project_id if it's not 'all'
  if (projectId && projectId !== 'all') {
    params.append('project_id', projectId);
  }

  return chatApi.get(`/chats?${params.toString()}`);
};

export const getChatMetadata = (chatId) => {
  return chatApi.get(`/chat/${chatId}`);
};

// History fetch. Pass { employeeId, shiftNumber } to resolve an AI-employee shift to its PostOffice
// without pre-knowing the shift's chat id (the backend looks it up + folds in that shift's credit
// block). Plain chatId-only calls are unchanged. The credit block rides in the response (.data.credits).
export const getChatHistory = (chatId, { employeeId, shiftNumber } = {}) => {
  const params = new URLSearchParams();
  if (employeeId != null) params.append('employee_id', employeeId);
  if (shiftNumber != null) params.append('shift_number', shiftNumber);
  const qs = params.toString();
  return chatApi.get(`/chat/${chatId}/history${qs ? `?${qs}` : ''}`);
};

export const getBulkChatStatus = (chatIds) => {
  return chatApi.post('/chats/status', {
    chat_ids: chatIds
  });
};

/**
 * Search across all the user's chats (names + message content).
 * @param {string} q - Search query (min 2 chars)
 * @param {Object} [options]
 * @param {string} [options.projectId] - Project ID filter ('' = personal only, null = all)
 * @param {string} [options.chatType] - 'all' | 'regular' | 'employee'
 * @param {number} [options.limit] - Max results (default 30)
 * @param {AbortSignal} [options.signal] - Abort in-flight request on new keystrokes
 * @returns {Promise<Object>} { results, degraded, took_ms }
 */
export const searchChats = (q, { projectId = null, chatType = 'all', limit = 30, sort = 'relevance', signal } = {}) => {
  const params = new URLSearchParams({ q, chat_type: chatType, limit: limit.toString(), sort });
  if (projectId !== null && projectId !== undefined) {
    params.append('project_id', projectId);
  }
  return chatApi.get(`/chats/search?${params.toString()}`, { signal });
};

export const createNewChat = (data) => {
  // Updated to accept object with message and optional project_id
  return chatApi.post('/chat/new', data);
};

// Unified settings pipeline: messages carry MESSAGES only. Tool/workflow approvals persist
// eagerly via updateChatSettings when changed — they no longer ride the send payload.
export const sendMessageToChat = (chatId, message) => {
  return chatApi.post(`/chat/${chatId}/messages`, { message });
};

/**
 * Update chat settings (name, project, agent role)
 * @param {string} chatId - Chat ID
 * @param {Object} settings - Settings to update
 * @param {string} [settings.name] - New chat name
 * @param {string} [settings.project_id] - Project ID to assign
 * @param {string} [settings.general_agent_role_id] - Agent role ID
 * @param {boolean} [settings.remove_project] - Remove project and role
 * @param {boolean} [settings.remove_agent_role] - Remove role only
 * @returns {Promise<Object>} Updated chat metadata
 */
export const updateChatSettings = (chatId, settings) => {
  return chatApi.put(`/chat/${chatId}/settings`, settings);
};

/**
 * Delete a chat permanently
 * @param {string} chatId - Chat ID to delete
 * @returns {Promise<Object>} Deletion response with task_id
 */
export const deleteChat = (chatId) => {
  return chatApi.delete(`/chat/${chatId}?confirm=true`);
};

/**
 * Get deletion status for a chat (fallback when WebSocket unavailable)
 * @param {string} chatId - Chat ID to check
 * @returns {Promise<Object>} Deletion status
 */
export const getDeletionStatus = (chatId) => {
  return chatApi.get(`/chat/${chatId}/deletion-status`);
};

/**
 * Delete a queued letterbox message by its index
 * @param {string} chatId - Chat ID
 * @param {number} index - Index of the message to delete from the queue
 * @returns {Promise<Object>} { success: true }
 */
export const deleteLetterboxMessage = (chatId, index) => {
  return chatApi.delete(`/chat/${chatId}/letterbox/messages/${index}`);
};

/**
 * Delete one of the owner's own messages from a chat (CEL-3474). The selector is the message exactly as the
 * history endpoint returned it: its createdAt plus a SHA-256 of its content (identical texts at different times,
 * and several transcripts per chat, resolve to the right one server-side).
 * @param {string} chatId - Chat ID (for an earlier part of an employee's history: that session's chat id)
 * @param {{createdAt: string, content: string}} message - The message from the history response
 * @returns {Promise<Object>} { deleted, scope, cost_warning_applied, message: <replacement bubble>, steps }
 */
export const deleteChatMessage = async (chatId, { createdAt, content }) => {
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(content));
  const contentSha256 = Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  return chatApi.delete(`/chat/${chatId}/messages`, {
    params: { created_at: createdAt, content_sha256: contentSha256 },
  });
};

// Authoritative letterbox snapshot (CEL-664): pending messages + per-viewer signed URLs.
// Fetched on the LETTERBOX_CHANGED WS notification (the event itself is content-free).
export const getLetterbox = (chatId) => {
  return chatApi.get(`/chat/${chatId}/letterbox`);
};

/**
 * Mark a chat as seen by the user (clears notification badge across all tabs/devices)
 * @param {string} chatId - Chat ID to mark as seen
 * @returns {Promise<Object>} { status: "ok" }
 */
export const markChatSeen = (chatId) => {
  return chatApi.patch(`/chat/${chatId}/seen`);
};

/**
 * Toggle app visibility between public and private
 * @param {string} chatId - Chat ID
 * @param {string} visibility - 'public' or 'private'
 * @returns {Promise<Object>} { success, visibility, app_url }
 */
export const toggleAppVisibility = (chatId, visibility) => {
  return chatApi.post(`/chat/${chatId}/app-visibility`, { visibility });
};

// Hire proposals (agent_docs/employee_initiated_hire_prd.md): the chat's ONE pending proposal slot.
// GET → { proposal: <PendingHireProposal>|null } — the "missed the WS event, refetch" recovery
// (the chat GET already carries `pending_hire_proposal` for the bootstrap).
export const getHireProposal = (chatId) => {
  return chatApi.get(`/chat/${chatId}/hire-proposal`);
};

// DELETE → { cleared: bool } — the owner's Cancel; clears the slot, silent to the proposer.
export const cancelHireProposal = (chatId) => {
  return chatApi.delete(`/chat/${chatId}/hire-proposal`);
};

export default chatApi;
